<template>
  <div>
    <v-row no-gutters align="center" class="mb-3" style="gap: 12px">
      <v-col cols="6" sm="4" md="3">
        <dropdown
          v-model="filterPeriod"
          :items="periodOptions"
          item-text="label"
          item-value="value"
          :label="$t('finance_date')"
          hide-details
        />
      </v-col>
      <v-col cols="6" sm="4" md="3">
        <dropdown
          v-model="filterType"
          :items="typeOptions"
          item-text="label"
          item-value="value"
          :label="$t('finance_type_label')"
          clearable
          hide-details
        />
      </v-col>
      <v-col cols="6" sm="4" md="3">
        <dropdown
          v-model="filterMoneyFlow"
          :items="moneyFlowOptions"
          item-text="label"
          item-value="value"
          :label="$t('finance_money_flow')"
          clearable
          hide-details
        />
      </v-col>
      <v-col v-if="serviceOptions.length" cols="6" sm="4" md="3">
        <dropdown
          v-model="filterServiceId"
          :items="serviceOptions"
          item-text="label"
          item-value="value"
          :label="$t('finance_service_label')"
          clearable
          hide-details
        />
      </v-col>
      <v-col cols="auto">
        <v-btn text small @click="resetFilters">{{ $t("finance_reset_filters") }}</v-btn>
      </v-col>
    </v-row>

    <v-row no-gutters align="center" class="mb-3" style="gap: 12px">
      <v-col cols="12" sm="6" md="4">
        <text-field
          v-model="search"
          :label="$t('finance_search_transactions')"
          kind="search"
          clearable
          hide-details
        />
      </v-col>
      <v-spacer />
      <v-col cols="auto">
        <span class="text-caption grey--text text--darken-1 mr-2">
          {{ $t("finance_selected_balance") }}
        </span>
        <span
          class="text-subtitle-1 font-weight-bold"
          :style="{ color: selectedBalance >= 0 ? '#00b37e' : '#d9534f' }"
        >
          {{ selectedBalance >= 0 ? "+" : "" }}{{ formatCurrency(selectedBalance) }}
        </span>
      </v-col>
    </v-row>

    <v-card outlined>
      <v-card-title class="text-subtitle-1 d-block">
        <div class="d-flex flex-wrap align-center" style="gap: 12px 20px">
          <span>{{ $t("finance_ledger_title") }}</span>
          <v-spacer />
          <span
            v-for="meta in moneyFlowLegend"
            :key="meta.value"
            class="d-flex align-center text-caption grey--text text--darken-2"
          >
            <span
              class="mf-dot mr-1"
              :style="{ background: meta.color }"
            />
            {{ meta.label }}
          </span>
        </div>
      </v-card-title>
      <v-data-table
        :headers="headers"
        :items="visibleTransactions"
        :loading="loading"
        :loading-text="$t('finance_loading')"
        mobile-breakpoint="0"
        :no-data-text="$t('finance_no_transactions_filtered')"
        item-key="id"
        class="striped-table"
      >
        <template v-slot:item.date="{ item }">{{ localeDate(item.date) }}</template>
        <template v-slot:item.type="{ item }">{{ describeType(item.entryType) }}</template>
        <template v-slot:item.moneyFlow="{ item }">
          <span
            v-if="moneyFlowMeta(item.moneyFlow)"
            class="d-flex align-center"
          >
            <span
              class="mf-dot mr-2"
              :style="{ background: moneyFlowMeta(item.moneyFlow).color }"
            />
            {{ $t(moneyFlowMeta(item.moneyFlow).labelKey) }}
          </span>
          <span v-else>—</span>
        </template>
        <template v-slot:item.debit="{ item }">
          <span style="color: #d9820f; font-weight: 600">
            {{ item.direction === "DEBIT" ? formatCurrency(item.amount) : "—" }}
          </span>
        </template>
        <template v-slot:item.credit="{ item }">
          <span style="color: #00b37e; font-weight: 600">
            {{ item.direction === "CREDIT" ? formatCurrency(item.amount) : "—" }}
          </span>
        </template>
      </v-data-table>
    </v-card>
  </div>
</template>

<script>
import restaurantFinanceService from "@/services/restaurantFinance";
import hasSnackbar from "@/mixins/hasSnackbar";
import { MONEY_FLOW_META } from "./constants";

// Isti kodovi kao Knjiga salda (description) - vidi ticket #223672 v2.
const KNOWN_TYPES = [
  "ORDER_REVENUE",
  "COMMISSION",
  "CASH_RECEIVED",
  "PAYOUT",
  "SUBSCRIPTION",
  "REFUND",
];

const TYPE_LABEL_KEYS = {
  ORDER_REVENUE: "finance_desc_order_revenue",
  COMMISSION: "finance_desc_commission",
  CASH_RECEIVED: "finance_desc_cash_received",
  PAYOUT: "finance_desc_payout",
  SUBSCRIPTION: "finance_desc_subscription",
  REFUND: "finance_desc_refund",
};

const mapTransactionDto = (dto) => ({
  id: dto.id,
  entryType: dto.entry_type,
  direction: dto.direction,
  amount: Math.abs(Number(dto.amount) || 0),
  currency: dto.currency,
  moneyFlow: dto.money_flow || null,
  sourceType: dto.source_type,
  sourceId: dto.source_id,
  note: dto.note,
  date: dto.created_at,
});

export default {
  name: "FinanceTabTransactions",

  mixins: [hasSnackbar],

  data: () => ({
    loading: true,
    transactions: [],
    serviceTypes: [],
    filterPeriod: "30d",
    filterType: null,
    filterMoneyFlow: null,
    filterServiceId: null,
    search: "",
  }),

  computed: {
    restaurantId() {
      return this.$store.state.restaurant?.id;
    },

    periodOptions() {
      return [
        { value: "7d", label: this.$t("finance_last_7_days") },
        { value: "30d", label: this.$t("finance_last_30_days") },
        { value: "90d", label: this.$t("finance_last_90_days") },
        { value: "all", label: this.$t("finance_all") },
      ];
    },

    typeOptions() {
      return KNOWN_TYPES.map((value) => ({ value, label: this.$t(TYPE_LABEL_KEYS[value]) }));
    },

    moneyFlowOptions() {
      return Object.keys(MONEY_FLOW_META).map((value) => ({
        value,
        label: this.$t(MONEY_FLOW_META[value].labelKey),
      }));
    },

    moneyFlowLegend() {
      return Object.keys(MONEY_FLOW_META).map((value) => ({
        value,
        color: MONEY_FLOW_META[value].color,
        label: this.$t(MONEY_FLOW_META[value].labelKey),
      }));
    },

    // Filter po usluzi - prikazuje se samo ako /service-types vrati nešto.
    serviceOptions() {
      return this.serviceTypes.map((st) => ({
        value: st.id,
        label: st.name || st.label || `#${st.id}`,
      }));
    },

    headers() {
      return [
        { text: this.$t("finance_date"), value: "date" },
        { text: this.$t("finance_type_label"), value: "type" },
        { text: this.$t("finance_description"), value: "note" },
        { text: this.$t("finance_money_flow"), value: "moneyFlow", sortable: false },
        { text: this.$t("finance_debit"), value: "debit", align: "end" },
        { text: this.$t("finance_credit"), value: "credit", align: "end" },
      ];
    },

    // Pretraga je lokalna (bez API poziva) - po opisu i broju narudžbe.
    visibleTransactions() {
      const term = (this.search || "").trim().toLowerCase();
      if (!term) return this.transactions;
      return this.transactions.filter((tx) => {
        const note = (tx.note || "").toLowerCase();
        const sourceId = String(tx.sourceId || "").toLowerCase();
        return note.includes(term) || sourceId.includes(term);
      });
    },

    // Saldo za trenutno prikazani skup - računa se iz već učitanih redova.
    // DEBIT umanjuje, CREDIT uvećava saldo restorana.
    selectedBalance() {
      return this.visibleTransactions.reduce((sum, tx) => {
        const signed = tx.direction === "DEBIT" ? -tx.amount : tx.amount;
        return sum + signed;
      }, 0);
    },
  },

  watch: {
    filterPeriod() {
      this.load();
    },
    filterType() {
      this.load();
    },
    filterMoneyFlow() {
      this.load();
    },
    filterServiceId() {
      this.load();
    },
  },

  created() {
    this.loadServiceTypes();
    this.load();
  },

  methods: {
    describeType(type) {
      const key = TYPE_LABEL_KEYS[type];
      return key ? this.$t(key) : (type || "").toLowerCase().replace(/_/g, " ");
    },

    moneyFlowMeta(value) {
      return value ? MONEY_FLOW_META[value] || null : null;
    },

    formatCurrency(value) {
      return `${Number(value || 0).toFixed(2)} ${this.restaurantCurrency}`;
    },

    resetFilters() {
      this.filterPeriod = "30d";
      this.filterType = null;
      this.filterMoneyFlow = null;
      this.filterServiceId = null;
      this.search = "";
    },

    async loadServiceTypes() {
      try {
        const { data } = await restaurantFinanceService.getServiceTypes();
        const list = Array.isArray(data) ? data : data?.data || [];
        this.serviceTypes = list.filter((st) => st && st.id != null);
      } catch (error) {
        // Endpoint možda još ne postoji - filter se jednostavno ne prikaže.
        this.serviceTypes = [];
      }
    },

    periodToFromDate() {
      if (this.filterPeriod === "all") return undefined;
      const days = { "7d": 7, "30d": 30, "90d": 90 }[this.filterPeriod];
      const from = new Date(Date.now() - days * 24 * 60 * 60 * 1000);
      return from.toISOString().slice(0, 10);
    },

    async load() {
      if (!this.restaurantId) return;

      this.loading = true;
      try {
        const { data } = await restaurantFinanceService.getTransactions(this.restaurantId, {
          type: this.filterType || undefined,
          money_flow: this.filterMoneyFlow || undefined,
          service_id: this.filterServiceId || undefined,
          from: this.periodToFromDate(),
        });
        this.transactions = (data?.data || []).map(mapTransactionDto);
      } catch (error) {
        this.showErrorSnackbar(this.$t("finance_transactions_load_error"));
      } finally {
        this.loading = false;
      }
    },
  },
};
</script>

<style scoped>
.mf-dot {
  width: 9px;
  height: 9px;
  border-radius: 50%;
  display: inline-block;
  flex: 0 0 auto;
}
</style>
